import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Play, RefreshCw, Loader2 } from 'lucide-react';
import Editor from '@monaco-editor/react';
import { useMutation } from '@tanstack/react-query';
import toolsService from '../services/toolsService';
import Toast from '../components/Toast';

const toolTypes = ['API', 'CODE'];
const httpMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const parameterTypes = ['String', 'Number', 'Boolean', 'Array', 'Object'];

const languageOptions = [{ label: 'Python', value: 'python' }];

const languageMap = {
  javascript: 63,
  typescript: 74,
  python: 71,
  c: 50,
  cpp: 54,
  java: 62,
  php: 68,
};

const inputClass =
  'w-full bg-white border border-shell-card-border rounded-lg px-3 py-2 text-sm text-[var(--text-color)] placeholder:text-muted-faint outline-none focus:border-primary/40';

let uid = 0;
const makeId = () => `row_${Date.now()}_${uid++}`;

const emptyDef = () => ({ id: makeId(), name: '', type: '', desc: '', defaultValue: '', required: 'false' });

function Section({ title, description, action, children }) {
  return (
    <div className="bg-shell-card border border-shell-card-border rounded-xl p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-color)]">{title}</h3>
          {description && <p className="text-xs text-muted mt-0.5">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-sm text-[var(--text-color)] mb-1.5 block">
        {required && <span className="text-[#EF4444]">*</span>} {label}
      </label>
      {children}
    </div>
  );
}

function AddRowButton({ onClick, label }) {
  return (
    <button type="button" onClick={onClick} className="flex items-center gap-1.5 text-xs text-primary hover:underline">
      <Plus className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function DefinitionList({ label, items, setItems }) {
  const update = (id, field, value) => {
    setItems(items.map(item => (item.id === id ? { ...item, [field]: value } : item)));
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={item.id} className="rounded-lg border border-shell-card-border bg-[#faf9f5] p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted">
              {label} {index + 1}
            </span>
            <button
              type="button"
              onClick={() => setItems(items.filter(i => i.id !== item.id))}
              className="text-muted-faint hover:text-[#EF4444] p-1">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label={`${label} Name`}>
              <input
                value={item.name}
                onChange={e => update(item.id, 'name', e.target.value)}
                placeholder={`Enter ${label.toLowerCase()} name`}
                className={inputClass}
              />
            </Field>
            <Field label="Type">
              <select value={item.type} onChange={e => update(item.id, 'type', e.target.value)} className={inputClass}>
                <option value="">Select type</option>
                {parameterTypes.map(t => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Default Value">
              <input
                value={item.defaultValue}
                onChange={e => update(item.id, 'defaultValue', e.target.value)}
                placeholder="Enter default value"
                className={inputClass}
              />
            </Field>
            <Field label="Required">
              <select value={item.required} onChange={e => update(item.id, 'required', e.target.value)} className={inputClass}>
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            </Field>
          </div>
          <Field label="Description">
            <input
              value={item.desc}
              onChange={e => update(item.id, 'desc', e.target.value)}
              placeholder={`Describe this ${label.toLowerCase()}...`}
              className={inputClass}
            />
          </Field>
        </div>
      ))}
    </div>
  );
}

function validateAndConvert(type, rawValue) {
  switch (type) {
    case 'Number':
      if (isNaN(Number(rawValue))) return { valid: false };
      return { valid: true, value: Number(rawValue) };
    case 'Boolean': {
      const normalized = String(rawValue).toLowerCase();
      if (!['true', 'false'].includes(normalized)) return { valid: false };
      return { valid: true, value: normalized === 'true' };
    }
    case 'Array':
      try {
        const parsed = JSON.parse(rawValue);
        if (!Array.isArray(parsed)) return { valid: false };
        return { valid: true, value: parsed };
      } catch {
        return { valid: false };
      }
    case 'Object':
      try {
        const parsed = JSON.parse(rawValue);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return { valid: false };
        return { valid: true, value: parsed };
      } catch {
        return { valid: false };
      }
    case 'String':
    default:
      return { valid: true, value: rawValue };
  }
}

function buildHeadersObject(headers) {
  return headers.reduce((acc, h) => {
    if (h.key && h.value) {
      let uniqueKey = h.key;
      let counter = 2;
      while (acc[uniqueKey]) {
        uniqueKey = `${h.key}${counter}`;
        counter++;
      }
      acc[uniqueKey] = h.value;
    }
    return acc;
  }, {});
}

const buildDefList = items =>
  items
    .filter(item => item.name && item.type)
    .map(item => ({
      name: item.name,
      desc: item.desc,
      required: item.required,
      type: item.type,
      default_value: item.defaultValue || null,
    }));

export default function CreateToolPage({ toolId }) {
  const navigate = useNavigate();
  const isEdit = !!toolId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [toolType, setToolType] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [method, setMethod] = useState('');
  const [headers, setHeaders] = useState([]);
  const [params, setParams] = useState([]);
  const [body, setBody] = useState([]);

  const [language, setLanguage] = useState('python');
  const [code, setCode] = useState('');
  const [running, setRunning] = useState(false);
  const [executionOutput, setExecutionOutput] = useState('');

  const [testSnapshot, setTestSnapshot] = useState(null);
  const [testValues, setTestValues] = useState({});
  const [testing, setTesting] = useState(false);
  const [testOutput, setTestOutput] = useState(null);

  const [detailLoading, setDetailLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        setDetailLoading(true);
        const resp = await toolsService.getToolDetails(toolId);
        const detail = resp?.detail;
        if (!detail) return;
        setName(detail.tool_name || '');
        setDescription(detail.description || '');
        setToolType(detail.tool_category || '');
        setEndpoint(detail.url || '');
        setMethod(detail.method || '');
        setHeaders(Object.entries(detail.headers || {}).map(([key, value]) => ({ id: makeId(), key, value })));
        setParams(
          (detail.params || []).map(p => ({
            id: makeId(),
            name: p.name || '',
            type: p.type || '',
            desc: p.desc || '',
            defaultValue: p.default_value ?? '',
            required: String(p.required ?? 'false'),
          })),
        );
        setBody(
          (detail.body || []).map(b => ({
            id: makeId(),
            name: b.name || '',
            type: b.type || '',
            desc: b.desc || '',
            defaultValue: b.default_value ?? '',
            required: String(b.required ?? 'false'),
          })),
        );
      } catch (error) {
        console.log(error);
      } finally {
        setDetailLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId]);

  const onMutationSuccess = () => {
    Toast({ type: 'success', message: isEdit ? 'Tool updated successfully' : 'Tool created successfully' });
    navigate('/tools');
  };
  const onMutationError = err => {
    Toast({ type: 'error', message: err?.detail || err?.message || 'Something went wrong' });
  };

  const createTool = useMutation({
    mutationFn: payload => toolsService.createTool(payload),
    onSuccess: onMutationSuccess,
    onError: onMutationError,
  });
  const updateTool = useMutation({
    mutationFn: payload => toolsService.updateTool(payload),
    onSuccess: onMutationSuccess,
    onError: onMutationError,
  });

  const handleSave = () => {
    if (!name.trim()) {
      Toast({ type: 'error', message: 'Tool name is required' });
      return;
    }
    if (!toolType) {
      Toast({ type: 'error', message: 'Tool Type is required' });
      return;
    }
    if (!description.trim()) {
      Toast({ type: 'error', message: 'Description is required' });
      return;
    }

    const payload = {
      ...(isEdit ? { tool_id: toolId } : {}),
      tool_name: name,
      description,
      tool_category: toolType,
      tool_type: 'user_created',
      url: endpoint,
      method,
      headers: buildHeadersObject(headers),
      params: buildDefList(params),
      body: buildDefList(body),
    };

    if (isEdit) updateTool.mutate(payload);
    else createTool.mutate(payload);
  };

  const handleFetchTestData = () => {
    setTestSnapshot({ endpoint, method, headers: [...headers], params: [...params], body: [...body] });
    const values = {};
    params.forEach(p => {
      values[`param_${p.id}`] = p.defaultValue ?? '';
    });
    body.forEach(b => {
      values[`body_${b.id}`] = b.defaultValue ?? '';
    });
    setTestValues(values);
  };

  const handleTest = async () => {
    if (!testSnapshot) {
      Toast({ type: 'info', message: 'Fetch data before testing the tool' });
      return;
    }
    setTesting(true);

    const paramsObj = {};
    for (const p of testSnapshot.params) {
      const raw = testValues[`param_${p.id}`];
      if (!p.name || raw === undefined || raw === '') continue;
      const { valid, value } = validateAndConvert(p.type, raw);
      if (!valid) {
        Toast({ type: 'error', message: `Invalid type for parameter "${p.name}". Expected ${p.type}.` });
        continue;
      }
      let uniqueKey = p.name;
      let counter = 2;
      while (uniqueKey in paramsObj) {
        uniqueKey = `${p.name}${counter}`;
        counter++;
      }
      paramsObj[uniqueKey] = value;
    }

    const bodyObj = {};
    for (const b of testSnapshot.body) {
      const raw = testValues[`body_${b.id}`];
      if (!b.name || raw === undefined || raw === '') continue;
      const { valid, value } = validateAndConvert(b.type, raw);
      if (!valid) {
        Toast({ type: 'error', message: `Invalid type for body parameter "${b.name}". Expected ${b.type}.` });
        continue;
      }
      let uniqueKey = b.name;
      let counter = 2;
      while (uniqueKey in bodyObj) {
        uniqueKey = `${b.name}${counter}`;
        counter++;
      }
      bodyObj[uniqueKey] = value;
    }

    try {
      const res = await toolsService.testTool({
        tool_name: name,
        description,
        tool_category: toolType,
        tool_type: 'user_created',
        url: testSnapshot.endpoint,
        method: testSnapshot.method,
        headers: buildHeadersObject(testSnapshot.headers),
        params: paramsObj,
        body: bodyObj,
      });
      setTestOutput(res);
    } catch (error) {
      console.log(error.message);
      Toast({ type: 'error', message: error.message });
      setTestOutput(error);
    } finally {
      setTesting(false);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    setExecutionOutput('Running your code...');
    try {
      const response = await fetch('https://ce.judge0.com/submissions?base64_encoded=false&wait=true', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          language_id: languageMap[language],
          source_code: code,
          stdin: '',
        }),
      });
      const result = await response.json();
      if (result.stderr) setExecutionOutput(result.stderr);
      else if (result.compile_output) setExecutionOutput(result.compile_output);
      else setExecutionOutput(result.stdout || 'No output');
    } catch (error) {
      setExecutionOutput('Error: ' + error.message);
    }
    setRunning(false);
  };

  const saving = createTool.isPending || updateTool.isPending;

  return (
    <div className="p-8 lg:p-10 max-w-4xl">
      <button
        type="button"
        onClick={() => navigate('/tools')}
        className="flex items-center gap-2 text-sm text-muted hover:text-[var(--text-color)] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Tools
      </button>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[var(--text-color)] mb-2 font-serif">{isEdit ? 'Edit Tool' : 'Create Tool'}</h1>
        <p className="text-muted">Define a tool with connection parameters so agents can call it during workflows.</p>
      </div>

      {detailLoading ? (
        <div className="space-y-6">
          <div className="flex items-center gap-3 text-sm text-muted">
            <Loader2 className="w-4 h-4 animate-spin text-primary" /> Loading tool details...
          </div>
          {[0, 1, 2].map(i => (
            <div key={i} className="bg-shell-card border border-shell-card-border rounded-xl p-5 space-y-4 animate-pulse">
              <div className="h-4 w-32 rounded bg-black/[0.04]" />
              <div className="grid md:grid-cols-2 gap-4">
                <div className="h-9 rounded-lg bg-black/[0.04]" />
                <div className="h-9 rounded-lg bg-black/[0.04]" />
              </div>
              <div className="h-20 rounded-lg bg-black/[0.04]" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          <Section title="Basics">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Tool Name" required>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Name your tool" className={inputClass} />
              </Field>
              <Field label="Tool Type" required>
                <select value={toolType} onChange={e => setToolType(e.target.value)} className={inputClass}>
                  <option value="">Select tool type</option>
                  {toolTypes.map(t => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Description" required>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder="What does this tool do and when should an agent use it?"
                className={`${inputClass} resize-none`}
              />
            </Field>
          </Section>

          {toolType === 'API' && (
            <>
              <Section title="API Configuration" description="Configure the API endpoint and request method">
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="md:col-span-3">
                    <Field label="API Endpoint">
                      <input
                        value={endpoint}
                        onChange={e => setEndpoint(e.target.value)}
                        placeholder="https://api.example.com/endpoint"
                        className={inputClass}
                      />
                    </Field>
                  </div>
                  <Field label="HTTP Method">
                    <select value={method} onChange={e => setMethod(e.target.value)} className={inputClass}>
                      <option value="">Select method</option>
                      {httpMethods.map(m => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </Section>

              <Section
                title="Request Headers"
                description="Custom headers to include with the request (e.g. Content-Type)."
                action={
                  <AddRowButton
                    onClick={() => setHeaders([...headers, { id: makeId(), key: '', value: '' }])}
                    label="Add header"
                  />
                }>
                {headers.length === 0 ? (
                  <p className="text-xs text-muted-faint">No headers added.</p>
                ) : (
                  <div className="space-y-2">
                    {headers.map(h => (
                      <div key={h.id} className="flex items-center gap-2">
                        <input
                          value={h.key}
                          onChange={e => setHeaders(headers.map(x => (x.id === h.id ? { ...x, key: e.target.value } : x)))}
                          placeholder="Authorization"
                          className={`${inputClass} flex-1`}
                        />
                        <input
                          value={h.value}
                          onChange={e => setHeaders(headers.map(x => (x.id === h.id ? { ...x, value: e.target.value } : x)))}
                          placeholder="Bearer token 123"
                          className={`${inputClass} flex-1`}
                        />
                        <button
                          type="button"
                          onClick={() => setHeaders(headers.filter(x => x.id !== h.id))}
                          className="text-muted-faint hover:text-[#EF4444] p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              <Section
                title="Parameters"
                description="Define the parameters that this tool accepts"
                action={<AddRowButton onClick={() => setParams([...params, emptyDef()])} label="Add parameter" />}>
                {params.length === 0 ? (
                  <p className="text-xs text-muted-faint">No parameters added.</p>
                ) : (
                  <DefinitionList label="Parameter" items={params} setItems={setParams} />
                )}
              </Section>

              <Section
                title="Body"
                description="Define the body that this tool accepts"
                action={<AddRowButton onClick={() => setBody([...body, emptyDef()])} label="Add body field" />}>
                {body.length === 0 ? (
                  <p className="text-xs text-muted-faint">No body fields added.</p>
                ) : (
                  <DefinitionList label="Body" items={body} setItems={setBody} />
                )}
              </Section>
            </>
          )}

          {toolType === 'CODE' && (
            <Section title="Code" description="Write the code this tool runs">
              <Field label="Language">
                <select value={language} onChange={e => setLanguage(e.target.value)} className={inputClass}>
                  {languageOptions.map(l => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="rounded-lg overflow-hidden border border-shell-card-border">
                <Editor
                  height="250px"
                  theme="vs-dark"
                  language={language}
                  value={code}
                  onChange={value => setCode(value)}
                  options={{
                    lineNumbers: 'on',
                    minimap: { enabled: false },
                    fontSize: 14,
                    lineHeight: 22,
                    padding: { top: 16, bottom: 16 },
                    smoothScrolling: true,
                    stickyScroll: { enabled: false },
                  }}
                />
              </div>
              <div>
                <p className="text-sm text-[var(--text-color)] mb-1.5">Output</p>
                <pre className="bg-[#faf9f5] border border-shell-card-border rounded-lg p-3 text-xs text-[var(--text-color)] whitespace-pre-wrap min-h-[64px] max-h-64 overflow-y-auto">
                  {executionOutput || 'Run the code to see output.'}
                </pre>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleRun}
                  disabled={running}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-primary hover:bg-primary/90 text-white disabled:opacity-50">
                  {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Test Code
                </button>
              </div>
            </Section>
          )}

          {toolType === 'API' && (
            <Section
              title="Test Tool"
              description="Make sure to fill all the fields and fetch data before testing tool"
              action={
                <button
                  type="button"
                  onClick={handleFetchTestData}
                  className="flex items-center gap-1.5 text-xs text-primary hover:underline">
                  <RefreshCw className="w-3.5 h-3.5" /> Fetch Data
                </button>
              }>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="rounded-lg bg-[#faf9f5] border border-shell-card-border p-3 md:col-span-2">
                  <p className="text-xs font-medium text-muted mb-1">API Endpoint</p>
                  <p className="text-sm text-[var(--text-color)] break-all">{testSnapshot?.endpoint || 'Null'}</p>
                </div>
                <div className="rounded-lg bg-[#faf9f5] border border-shell-card-border p-3">
                  <p className="text-xs font-medium text-muted mb-1">HTTP Method</p>
                  <p className="text-sm text-[var(--text-color)]">{testSnapshot?.method || 'Null'}</p>
                </div>
              </div>

              {testSnapshot?.headers?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted mb-2">Headers</p>
                  <div className="space-y-1">
                    {testSnapshot.headers.map(h => (
                      <p key={h.id} className="text-sm text-[var(--text-color)]">
                        <span className="text-muted">{h.key || 'Null'}:</span> {h.value || 'Null'}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {testSnapshot?.params?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted mb-2">Parameters</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    {testSnapshot.params.map(p => (
                      <Field key={p.id} label={p.name || 'Unnamed parameter'}>
                        <input
                          value={testValues[`param_${p.id}`] ?? ''}
                          onChange={e => setTestValues(v => ({ ...v, [`param_${p.id}`]: e.target.value }))}
                          placeholder="Enter value to proceed"
                          className={inputClass}
                        />
                      </Field>
                    ))}
                  </div>
                </div>
              )}

              {testSnapshot?.body?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted mb-2">Body</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    {testSnapshot.body.map(b => (
                      <Field key={b.id} label={b.name || 'Unnamed field'}>
                        <input
                          value={testValues[`body_${b.id}`] ?? ''}
                          onChange={e => setTestValues(v => ({ ...v, [`body_${b.id}`]: e.target.value }))}
                          placeholder="Enter value to proceed"
                          className={inputClass}
                        />
                      </Field>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm text-[var(--text-color)] mb-1.5">Output</p>
                <pre className="bg-[#faf9f5] border border-shell-card-border rounded-lg p-3 text-xs text-[var(--text-color)] whitespace-pre-wrap min-h-[64px] max-h-64 overflow-y-auto">
                  {testOutput
                    ? JSON.stringify(testOutput?.data ?? testOutput, null, 2)
                    : 'Fetch data and run a test to see output.'}
                </pre>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleTest}
                  disabled={testing}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-primary hover:bg-primary/90 text-white disabled:opacity-50">
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Test Tool
                </button>
              </div>
            </Section>
          )}
        </div>
      )}

      <div className="mt-8 flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate('/tools')}
          className="px-6 py-2.5 rounded-lg text-sm text-muted hover:text-[var(--text-color)] border border-shell-card-border">
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || detailLoading}
          className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium bg-primary hover:bg-primary/90 text-white disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : isEdit ? 'Update Tool' : 'Create Tool'}
        </button>
      </div>
    </div>
  );
}
